/**
 * Submission utility functions for frontend
 */

/**
 * Extracts timestamp from a submission unique ID
 * The unique ID format: 8-char timestamp (base36) + 24-char random (base36)
 * @param uniqueId - The submission unique ID
 * @returns Date object or null if invalid
 */
export function extractTimestampFromUniqueId(uniqueId: string): number | null {
  if (!uniqueId || typeof uniqueId !== 'string') return null;
  
  // Check if it's a valid format (32 characters, alphanumeric)
  if (uniqueId.length !== 32 || !/^[A-Z0-9]{32}$/i.test(uniqueId)) {
    return null;
  }
  
  try {
    // Extract first 8 characters (timestamp part)
    const timestampStr = uniqueId.slice(0, 8).toLowerCase();
    
    // Convert from base36 to timestamp (seconds)
    const timestamp = parseInt(timestampStr, 36);
    
    // Convert to milliseconds and create Date object
    return timestamp;
  } catch (error) {
    console.error('Error extracting timestamp from unique ID:', error);
    return null;
  }
}

/**
 * Gets a human-readable submission timestamp from unique ID
 * @param uniqueId - The submission unique ID
 * @returns Formatted timestamp string or null
 */
export function getSubmissionTimestamp(uniqueId: string): string | null {
  const timestamp = extractTimestampFromUniqueId(uniqueId);
  return timestamp ? timestamp.toString() : null;
}
